import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Card,
  Button,
  Modal,
  Tabs,
  Tab,
  Row,
  Col,
} from "react-bootstrap";
import { DateTime } from "luxon";
import MeetingCard from "./MeetingCard";
import PastRecordings from "./PastRecordings";
const BASE_URL =
  "https://opensheet.elk.sh/11R9T54CjmC2RFZuvmD7fxElItomKuvfTb9t5nHjeWNU";
const SHEETS = {
  COURSES: `${BASE_URL}/Courses`,
  ZOOM: `${BASE_URL}/ZoomSessions`,
  READINGS: `${BASE_URL}/ReadingMaterials`,
};

const formatSessionTime = (dateStr, timeStr, userTz) => {
  // dateStr: "27-May-25"
  // timeStr: "8pm - 10pm" or "11:30am - 1:30pm"
  const [startRaw, endRaw] = timeStr.toLowerCase().split(" - ");
  const startTime = parseTime(startRaw, dateStr, userTz);
  const endTime = parseTime(endRaw, dateStr, userTz);

  const formattedDate = startTime.toFormat("dd-MMM-yy");
  const formattedRange = `${startTime.toFormat("h:mma")} - ${endTime.toFormat(
    "h:mma"
  )}`;

  return { formattedDate, formattedRange };
};
const parseTime = (time, dateStr, userTz) => {
  // Normalize time input
  let normalizedTime = time.trim().toLowerCase();
  normalizedTime = normalizedTime.replace(/am|pm/, (match) =>
    match.toUpperCase()
  );

  if (!normalizedTime.includes(":")) {
    normalizedTime = normalizedTime.replace(/(AM|PM)$/, ":00$1");
  }

  const dateTimeFormats = ["dd-MMM-yy h:mma", "d-MMM-yy h:mma"];
  const dateTimeStr = `${dateStr} ${normalizedTime}`;

  let dt;
  for (const format of dateTimeFormats) {
    dt = DateTime.fromFormat(dateTimeStr, format, {
      zone: "Asia/Kolkata",
    });
    if (dt.isValid) break;
  }

  if (!dt || !dt.isValid) {
    console.error("Failed to parse datetime:", dateTimeStr);
    return null;
  }

  return dt.setZone(userTz);
};
const CourseView = () => {
  const { courseId } = useParams();
  const [courseInfo, setCourseInfo] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [readings, setReadings] = useState([]);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [courseMeta, setCourseMeta] = useState({});
  const [readingMap, setReadingMap] = useState({});
  const [key, setKey] = useState("overview");

  useEffect(() => {
    const safeCourseId = courseId?.trim().toLowerCase();

    fetch(SHEETS.COURSES)
      .then((res) => res.json())
      .then((data) => {
        const info = data.find(
          (c) => (c.CourseId || "").trim().toLowerCase() === safeCourseId
        );
        setCourseInfo(info);
      });

    fetch(SHEETS.ZOOM)
      .then((res) => res.json())
      .then((data) => {
        const relevant = data.filter(
          (s) => (s.CourseId || "").trim().toLowerCase() === safeCourseId
        );
        setSessions(relevant);
      });

    fetch(SHEETS.READINGS)
      .then((res) => res.json())
      .then((data) => {
        const relevant = data.filter(
          (r) => (r.CourseId || "").trim().toLowerCase() === safeCourseId
        );
        setReadings(relevant);
      });
  }, [courseId]);

  const now = new Date();
  const upcoming = sessions.filter((s) => new Date(s.Date) >= now);
  const pastRecordings = sessions.filter(
    (s) =>
      new Date(s.Date) < now && s.RecordingLink && s.RecordingLink.trim() !== ""
  );

  const normalizeTitle = (title) =>
    title
      ?.replace(/^\d+\.\s*/, "")
      .replace(/\.[^/.]+$/, "")
      .toLowerCase()
      .trim();

  const formatRecordingLink = (url) => {
    if (!url) return "";
    const match = url.match(
      /https:\/\/drive\.google\.com\/file\/d\/([^/]+)\/(view|preview)/
    );
    if (match) return `https://drive.google.com/file/d/${match[1]}/preview`;
    return url;
  };
  const renderPrereads = (session) => {
    const courseId = session.CourseId;
    const prereadsRaw = session.Prereads?.trim();
    if (!prereadsRaw) return null;

    const prereads = prereadsRaw
      .split("\n")
      .map((p) => p.trim())
      .filter(Boolean);
    if (prereads.length === 0) return null;

    const readings = readingMap[courseId] || [];

    const matchedLinks = prereads.map((pre, idx) => {
      const normPre = normalizeTitle(pre);
      const match = readings.find((r) =>
        normalizeTitle(r.title).includes(normPre)
      );
      return match ? (
        <li key={`match-${idx}`}>
          <a href={match.link} target="_blank" rel="noreferrer">
            {match.title}
          </a>
        </li>
      ) : (
        <li key={`unmatched-${idx}`}>{pre}</li>
      );
    });

    return (
      <div>
        <strong>Prereads:</strong>
        <ul>{matchedLinks}</ul>
      </div>
    );
  };

  const openPdf = (url) => {
    setSelectedPdf(url);
    setShowPdfModal(true);
  };
  const decodeGoogleRedirect = (url) => {
    try {
      const parsedUrl = new URL(url);
      if (
        parsedUrl.hostname === "www.google.com" &&
        parsedUrl.pathname === "/url"
      ) {
        const actualUrl = parsedUrl.searchParams.get("q");
        return actualUrl ? decodeURIComponent(actualUrl) : url;
      }
    } catch (e) {
      // If URL parsing fails, return as is
    }
    return url;
  };
  const handleVideoClick = (url) => {
    window.open(formatRecordingLink(url), "_blank", "noopener,noreferrer");
  };

  return (
    <>
      {" "}
      <style>{`
      .force-margin {
        margin-top: 66px !important;
      }
    `}</style>
      <Container className="mt-4 force-margin">
        <h2>Course: {courseInfo?.CourseName || courseId}</h2>

        <Tabs activeKey={key} onSelect={(k) => setKey(k)} className="mb-3">
          <Tab eventKey="overview" title="Overview">
            {courseInfo ? (
              <Card className="mb-4">
                <Card.Body>
                  <Card.Title>{courseInfo.CourseName}</Card.Title>
                  <p>
                    <strong>Faculty:</strong> {courseInfo.Faculty}
                  </p>
                  {courseInfo.FacultyEmail && (
                    <p>
                      <strong>Email:</strong> {courseInfo.FacultyEmail}
                    </p>
                  )}
                  <a
                    href={courseInfo.GoogleDriveFolder}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-secondary"
                  >
                    Drive Folder
                  </a>
                </Card.Body>
              </Card>
            ) : (
              <p>Loading course info...</p>
            )}
          </Tab>

          <Tab eventKey="sessions" title="Sessions">
            <Row>
              <Col md={6}>
                <h5>Upcoming Sessions</h5>
                {upcoming.length === 0 && <p>No upcoming sessions found.</p>}
                {upcoming.map((s, idx) => (
                  <MeetingCard
                    key={`upcoming-${idx}`}
                    session={s}
                    meta={courseMeta[s.CourseId] || {}}
                    renderPrereads={renderPrereads} // Optional: Only if needed
                    decodeGoogleRedirect={decodeGoogleRedirect}
                    formatRecordingLink={formatRecordingLink}
                    formatSessionTime={formatSessionTime}
                  />
                ))}
              </Col>

              <Col md={6}>
                <PastRecordings
                  pastWithRecording={pastRecordings}
                  selectedCourse={courseId}
                  handleVideoClick={handleVideoClick}
                  formatSessionTime={formatSessionTime}
                />
              </Col>
            </Row>
          </Tab>

          <Tab eventKey="readings" title="Reading Materials">
            {readings.length === 0 ? (
              <p>No reading materials found.</p>
            ) : (
              <Tabs className="mb-3">
                {Object.entries(
                  readings.reduce((acc, r) => {
                    const format = (r.Format || "Other").trim();
                    if (!acc[format]) acc[format] = [];
                    acc[format].push(r);
                    return acc;
                  }, {})
                ).map(([format, items]) => (
                  <Tab eventKey={format} title={format} key={format}>
                    <Row>
                      {items.map((r, idx) => (
                        <Col md={6} key={idx} className="mb-3">
                          <Card>
                            <Card.Body>
                              <Card.Title>{`${idx + 1}. ${
                                r.Title
                              }`}</Card.Title>
                              <p>
                                <strong>Faculty:</strong> {r.Faculty}
                              </p>
                              <Button
                                variant="outline-secondary"
                                onClick={() => openPdf(r.ReadingMaterialLink)}
                              >
                                View
                              </Button>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </Tab>
                ))}
              </Tabs>
            )}
          </Tab>
        </Tabs>

        {/* Fullscreen PDF Viewer Modal */}
        <Modal
          show={showPdfModal}
          onHide={() => setShowPdfModal(false)}
          fullscreen
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Reading Material</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: 0 }}>
            {selectedPdf && (
              <iframe
                src={selectedPdf.replace("/view?", "/preview?")}
                width="100%"
                height="100%"
                style={{ border: "none", minHeight: "80vh" }}
                title="Reading Material PDF"
              />
            )}
          </Modal.Body>
        </Modal>
      </Container>
    </>
  );
};

export default CourseView;
//p@55Woro!
